import { type ReactNode, useCallback, useEffect, useState } from 'react';
import Header from '@components/Layout/Header';
import Footer from '@components/Layout/Footer';
import SearchOverlay from '@components/Layout/SearchOverlay';
import LoadingSpinner, { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import Toast from '@components/Layout/Toast';
import ContainerSizeController from '@components/Layout/ContainerSizeController';
import { AlertContainer } from '@components/UI';
import { useAuth } from '@context/AuthContext';
import { hideInitialSpinner } from '@utils/spinnerControl';
import { ChatWidget } from '@/components/AgentChat/ChatWidget';
import { ChatCommandPalette } from '@/components/AgentChat/ChatCommandPalette';
import { useGlobalKeyboardShortcuts } from '@/hooks/useGlobalKeyboardShortcuts';
import { Navigate, useLocation } from 'react-router-dom';
import { buildAgreementRedirectPath, isAgreementRoute } from '@/utils/agreements';
import SupportSessionBanner from '@/components/Support/SupportSessionBanner';

interface LayoutProps {
    children: ReactNode;
    title?: string;
    hideHeaderUntilAuthenticated?: boolean;
    containerSize?: string;
}

const Layout = ({ children, title = 'Project 314', hideHeaderUntilAuthenticated = true, containerSize }: LayoutProps) => {
    // Get authentication state
    const {
        isAuthenticated,
        isImpersonating,
        isLoading: authLoading,
        hasPendingRequiredAgreements,
        pendingRequiredAgreements,
        sessionPolicy
    } = useAuth();

    const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        document.title = title;
    }, [title]);

    const handleCommandK = useCallback(() => {
        setIsCommandPaletteOpen((prev) => !prev);
    }, []);

    const openSearch = useCallback(() => setIsSearchOpen(true), []);
    const closeSearch = useCallback(() => setIsSearchOpen(false), []);

    useGlobalKeyboardShortcuts({ onCommandK: handleCommandK, enabled: isAuthenticated && sessionPolicy.canUseAgentChat });
    useGlobalKeyboardShortcuts({ onSlash: openSearch });

    // Show loading spinner while authentication is being checked
    useEffect(() => {
        if (hideHeaderUntilAuthenticated && authLoading) {
            // Keep both spinners visible while authentication is loading
            showSpinner();
        } else {
            // Hide both spinners when authentication is complete
            hideSpinner();
            hideInitialSpinner();
        }

        return () => {
            hideSpinner();
            hideInitialSpinner();
        };
    }, [authLoading, hideHeaderUntilAuthenticated]);

    const isAgreementExemptRoute =
        isAgreementRoute(location.pathname) ||
        location.pathname === '/terms-and-conditions' ||
        location.pathname === '/privacy-policy' ||
        location.pathname === '/login' ||
        location.pathname === '/register';

    if (!authLoading && isAuthenticated && !isImpersonating && hasPendingRequiredAgreements && !isAgreementExemptRoute) {
        return (
            <Navigate
                replace
                to={buildAgreementRedirectPath({
                    pendingRequiredAgreements,
                    redirectTarget: location.pathname + location.search
                })}
            />
        );
    }

    return (
        <div className="container mx-auto min-h-screen flex flex-col">
            <ContainerSizeController responsivePadding size={containerSize} />
            {/* Only render the header when authentication check is complete */}
            {(!hideHeaderUntilAuthenticated || !authLoading) && <Header onSearchClick={openSearch} />}
            <SupportSessionBanner />
            <main className="flex-1">{children}</main>
            <Footer />
            {isSearchOpen && <SearchOverlay onClose={closeSearch} />}
            <LoadingSpinner />
            <Toast />
            <AlertContainer maxAlerts={5} position="top-right" />
            {sessionPolicy.canUseAgentChat && <ChatWidget />}
            {isAuthenticated && sessionPolicy.canUseAgentChat && (
                <ChatCommandPalette isOpen={isCommandPaletteOpen} onClose={() => setIsCommandPaletteOpen(false)} />
            )}
        </div>
    );
};

export default Layout;
